/**
 * The page as the serial link: browser port on one end, vehicle link on the
 * other, `/ws/serial-bridge` in between.
 *
 * The bridge lives here rather than in the dialog's state on purpose. Closing
 * the connect dialog must not drop the aircraft — the operator opens that dialog
 * to add a link and then goes back to flying — so the running session is module
 * state the dialog subscribes to, and only an explicit DISCONNECT ends it.
 *
 * Both directions matter. Frames off the radio go up; everything the server
 * writes back (the 1 Hz GCS heartbeat, stream requests, every command) comes
 * down and out of the port. A one-way bridge produces an aircraft on the map
 * that silently refuses to be flown.
 */

import { wsUrl } from '@/config';
import { getAccessToken } from '@/stores/authStore';
import { WS_TOKEN_PROTOCOL } from '@/api/ws';

import {
  HeartbeatProbe,
  openSerialMavlink,
  type ProbeStatus,
  type SerialSession,
} from './webSerialBridge';

/** Baud rates worth offering. 57600 is the SiK/RFD default and the right first guess. */
export const BAUD_RATES = [57600, 115200, 921600] as const;

export interface BridgeParams {
  droneId: string;
  callsign: string;
  sysId: number;
  baudRate: number;
}

export interface BridgeSocket {
  send: (bytes: Uint8Array) => void;
  close: () => void;
  /** Bytes the server wrote back. Everything here has to reach the radio. */
  onBytes: (handler: (bytes: Uint8Array) => void) => void;
  /** The link went away without us asking, with the server's close code. */
  onClosed: (handler: (code: number, reason: string) => void) => void;
}

/**
 * The server deleted this bridge's drone and let go on purpose. Mirrors
 * CLOSE_DRONE_REMOVED in api/app/routers/serial_bridge.py.
 */
export const CLOSE_DRONE_REMOVED = 4004;

/** What to tell the operator when the server closes a bridge. */
export function closeMessage(code: number, reason: string): string {
  if (code === CLOSE_DRONE_REMOVED) {
    return 'The drone was deleted, so the radio was released. Choose the port again to reconnect.';
  }
  if (reason !== '') return reason;
  return 'The server closed the bridge.';
}

export interface SerialBridgeState {
  running: boolean;
  droneId: string;
  label: string;
  baudRate: number;
  status: ProbeStatus;
  error: string | null;
}

const IDLE_STATUS: ProbeStatus = { bytes: 0, frames: 0, sysId: null, verdict: 'silent' };

const IDLE: SerialBridgeState = {
  running: false,
  droneId: '',
  label: '',
  baudRate: 57600,
  status: IDLE_STATUS,
  error: null,
};

let state: SerialBridgeState = IDLE;
const listeners = new Set<() => void>();
let running: { session: SerialSession; socket: BridgeSocket } | null = null;

function publish(next: Partial<SerialBridgeState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

export function subscribeSerialBridge(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable between changes, as useSyncExternalStore requires. */
export function serialBridgeState(): SerialBridgeState {
  return state;
}

/**
 * The device label the server records for this link.
 *
 * Narrowed to the character set the endpoint accepts, because a rejected query
 * parameter closes the socket with 1008 and no explanation anywhere — the
 * operator would see "refused" and have nothing to act on.
 */
export function deviceTag(label: string): string {
  const cleaned = label
    .replace(/[^A-Za-z0-9 ._/-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40)
    .trim();
  return cleaned === '' ? 'usb' : cleaned;
}

/**
 * Open a byte bridge socket and wait for it to be accepted.
 *
 * Shared by the one-drone USB bridge and the mesh bridge. ``refused`` is what
 * the operator reads when the server closes before accepting, which is how an
 * authorisation failure looks from here.
 */
export async function openBridgeSocket(
  path: string,
  query: URLSearchParams,
  refused: string,
): Promise<BridgeSocket> {
  const token = getAccessToken();
  if (token === null) throw new Error('This session has no access token. Sign in again.');

  // The token rides in the subprotocol, never the query string: nginx writes
  // query strings to access.log verbatim.
  const socket = new WebSocket(wsUrl(`${path}?${query.toString()}`), [WS_TOKEN_PROTOCOL, token]);
  socket.binaryType = 'arraybuffer';

  await new Promise<void>((resolve, reject) => {
    socket.onopen = () => resolve();
    socket.onclose = () => reject(new Error(refused));
  });

  return {
    send: (bytes) => socket.send(bytes),
    close: () => socket.close(),
    onBytes: (handler) => {
      socket.onmessage = (event: MessageEvent) => {
        if (event.data instanceof ArrayBuffer) handler(new Uint8Array(event.data));
      };
    },
    onClosed: (handler) => {
      socket.onclose = (event: CloseEvent) => handler(event.code, event.reason);
    },
  };
}

function openWebSocket(params: BridgeParams, device: string): Promise<BridgeSocket> {
  const query = new URLSearchParams({
    droneId: params.droneId,
    callsign: params.callsign,
    sysId: String(params.sysId),
    baud: String(params.baudRate),
    device,
  });
  return openBridgeSocket(
    '/ws/serial-bridge',
    query,
    'The server refused the bridge. Check that you may command this drone id.',
  );
}

interface Deps {
  openPort: (baudRate: number) => Promise<SerialSession>;
  openSocket: (params: BridgeParams, device: string) => Promise<BridgeSocket>;
  now: () => number;
}

const defaults: Deps = {
  openPort: openSerialMavlink,
  openSocket: openWebSocket,
  now: () => Date.now(),
};

// The counter is watched, not read once, so it has to move — but a serial read
// can land 50 times a second and each one would re-render the dialog.
const STATUS_INTERVAL_MS = 200;

/**
 * Open the port, open the socket, and pump until either end closes.
 *
 * Resolves once the link is up and pumping; the pump itself runs on after that.
 * Rejects if the operator dismissed the port picker or the server refused, and
 * either way leaves nothing half-open.
 */
export async function startSerialBridge(
  params: BridgeParams,
  overrides: Partial<Deps> = {},
): Promise<void> {
  const deps = { ...defaults, ...overrides };
  if (running !== null) throw new Error('A serial bridge is already running.');

  publish({ ...IDLE, running: false, error: null, baudRate: params.baudRate });
  const session = await deps.openPort(params.baudRate);

  let socket: BridgeSocket;
  try {
    socket = await deps.openSocket(params, deviceTag(session.label));
  } catch (error) {
    await session.close();
    throw error;
  }

  running = { session, socket };
  publish({
    running: true,
    droneId: params.droneId,
    label: session.label,
    baudRate: params.baudRate,
    status: IDLE_STATUS,
    error: null,
  });

  socket.onBytes((bytes) => {
    void session.write(bytes).catch(() => {
      // The port went away mid-write; the read loop below sees the same thing
      // and owns the teardown. Racing it from here would close twice.
    });
  });
  socket.onClosed((code, reason) => {
    // stopSerialBridge closes the port as well as the socket, which is what
    // releases the USB device when the drone behind it was deleted.
    void stopSerialBridge(closeMessage(code, reason));
  });

  void pump(session, socket, deps.now);
}

async function pump(
  session: SerialSession,
  socket: BridgeSocket,
  now: () => number,
): Promise<void> {
  const probe = new HeartbeatProbe();
  let lastPublish = 0;
  try {
    for await (const chunk of session.chunks) {
      for (const frame of probe.push(chunk)) socket.send(frame.bytes);
      const moment = now();
      if (moment - lastPublish >= STATUS_INTERVAL_MS) {
        lastPublish = moment;
        publish({ status: probe.status() });
      }
    }
    publish({ status: probe.status() });
  } catch (error) {
    publish({ status: probe.status(), error: describe(error) });
  } finally {
    // A port that stops reading is a radio that was unplugged. Tear the link
    // down rather than leaving a vehicle in the fleet with nothing behind it.
    if (running !== null && running.session === session) await stopSerialBridge();
  }
}

export async function stopSerialBridge(reason: string | null = null): Promise<void> {
  const current = running;
  running = null;
  if (current === null) {
    if (reason !== null) publish({ running: false, error: reason });
    return;
  }
  current.socket.close();
  await current.session.close().catch(() => {});
  publish({ running: false, status: IDLE_STATUS, error: reason });
}

export function describe(error: unknown): string {
  if (error instanceof Error) {
    // The port picker rejects with NotFoundError when the operator dismisses it,
    // which is not a failure worth showing in red.
    if (error.name === 'NotFoundError') return 'No port chosen.';
    if (error.name === 'NetworkError') return 'The port could not be opened. Is it in use?';
    return error.message;
  }
  return String(error);
}

/** Test seam: drop any running session without touching the real hardware. */
export const __testing = {
  reset: (): void => {
    running = null;
    state = IDLE;
    listeners.clear();
  },
};
